<template>
  <div
    class="medication-frequency"
    :class="{'medication-frequency__wrapper': period != 'if_needed'}"
  >
    <div v-if="!isEditable">
      <div :class="['medication-frequency__input', ...inputClass]">
        {{ humanReadableFrequency }}
      </div>
    </div>
    <div v-else>
      <div
        class="d-flex align-center medication-frequency__title"
        v-if="period !== 'if_needed'"
      >
        <p>
          {{$ucfirst($t('frequencyValues.beingRepeatedFor', {catalog : 'app_mobile'}))}}
        </p>
      </div>
      <div class="d-flex" :class="period === 'D' ? 'mt-5' : period === 'if_needed' ? 'mt-0' : 'mt-1'">
        <integer-input
            class="medication-frequency__repeated-for"
            v-model="beingRepeatedForEvery"
            outlined
            dense
            v-show="period != 'if_needed'"
            hideDetails
            :error="(+beingRepeatedForEvery <= 0
              || Number.isNaN(Number(beingRepeatedForEvery))) && showErrors"
            :min="0"
        ></integer-input>
        <v-select
            class="medication-frequency__repeated-for-period"
            v-model="period"
            :items="periodOptions"
            outlined
            dense
            hide-details
        ></v-select>
      </div>
      <v-select
          v-if="period === 'Y'"
          v-model="monthsOfYear"
          :items="monthsOfYearOptions"
          :label="$ucfirst($t('frequencyValues.in', {catalog: 'app_mobile'}))"
          outlined
          dense
          hide-details
          class="mt-5"
          :error="!monthsOfYear && showErrors"
      ></v-select>
      <v-select
          v-if="((period === 'Y' && monthsOfYear != null) || period === 'M')"
          v-model="daysOfMonth"
          :items="daysOfMonthOptions()"
          :label="$ucfirst($t('frequencyValues.onThe', {catalog: 'app_mobile'}))"
          outlined
          dense
          hide-details
          class="mt-5"
          :error="!daysOfMonth && showErrors"
      ></v-select>
      <v-select
          v-if="period === 'W'"
          v-model="daysOfWeek"
          :items="daysOfWeekOptions"
          :label="$ucfirst($t('frequencyValues.repeatsOn', {catalog: 'app_mobile'}))"
          outlined
          multiple
          dense
          hide-details
          class="mt-5"
          :error="!daysOfWeek.length && showErrors"
      ></v-select>
    </div>
  </div>
</template>

<script>
import IntegerInput from '../IntegerInput';
import moment from "moment";

/**
 * Component MedicationFrequency.vue
 *
 * @module MedicationFrequency
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.10] app/src/APP_DASHBOARD/templates/components/Parts/Medication/MedicationFrequency.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'MedicationFrequency',
  components: {
    IntegerInput,
  },
  props: {
    schedule: {
      // type: Object,
      required: true,
    },
    isEditable: {
      type: Boolean,
      required: true,
    },
    showErrors: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      intervals: [],
      monthsOfYear: null,
      daysOfMonth: null,
      daysOfWeek: [],
      beingRepeatedForEvery: 1,
      period: 'D',
    };
  },
  computed: {
    inputClass() {
      return this.isEditable ? [] : ['medication-frequency__input_disabled']
    },
    periodOptions() {
      return [
        {text: this.$ucfirst(this.$t('frequency.days')), value: 'D'},
        {text: this.$ucfirst(this.$t('frequency.weeks')), value: 'W'},
        {text: this.$ucfirst(this.$t('frequency.months')), value: 'M'},
        {text: this.$ucfirst(this.$t('frequency.years')), value: 'Y'},
        {text: this.$ucfirst(this.$t('frequencyValues.ifNeeded', {catalog: 'app_mobile'})), value: "if_needed"},
      ];
    },
    monthsOfYearOptions() {
      return [
        {text: this.$ucfirst(this.$t("date.MMMM-01")), value: 1},
        {text: this.$ucfirst(this.$t("date.MMMM-02")), value: 2},
        {text: this.$ucfirst(this.$t("date.MMMM-03")), value: 3},
        {text: this.$ucfirst(this.$t("date.MMMM-04")), value: 4},
        {text: this.$ucfirst(this.$t("date.MMMM-05")), value: 5},
        {text: this.$ucfirst(this.$t("date.MMMM-06")), value: 6},
        {text: this.$ucfirst(this.$t("date.MMMM-07")), value: 7},
        {text: this.$ucfirst(this.$t("date.MMMM-08")), value: 8},
        {text: this.$ucfirst(this.$t("date.MMMM-09")), value: 9},
        {text: this.$ucfirst(this.$t("date.MMMM-10")), value: 10},
        {text: this.$ucfirst(this.$t("date.MMMM-11")), value: 11},
        {text: this.$ucfirst(this.$t("date.MMMM-12")), value: 12},
      ];
    },
    daysOfWeekOptions() {
      return [
        {text: this.$ucfirst(this.$t("date.monday")), value: 1},
        {text: this.$ucfirst(this.$t("date.tuesday")), value: 2},
        {text: this.$ucfirst(this.$t("date.wednesday")), value: 3},
        {text: this.$ucfirst(this.$t("date.thursday")), value: 4},
        {text: this.$ucfirst(this.$t("date.friday")), value: 5},
        {text: this.$ucfirst(this.$t("date.saturday")), value: 6},
        {text: this.$ucfirst(this.$t("date.sunday")), value: 7},
      ];
    },
    humanReadableFrequency() {
      return this.$app.scheduleManager.scheduleToString(this.schedule);
    },
  },
  watch: {
    humanReadableFrequency() {
      this.$emit('frequencyChanged')
    }
  },
  methods: {
    daysOfMonthOptions: function () {
      let maxDaysMonth = 31;
      if (this.period !== 'M' && this.monthsOfYear != null) {
        // Get max days from selected month
        maxDaysMonth = moment(moment().month(this.monthsOfYear - 1)).daysInMonth();
      }
      const days = Array.from({length: maxDaysMonth}, (_, i) => i + 1);

      // If selected day is out of range of new month, set it to the last possible day
      if (this.daysOfMonth > maxDaysMonth) {
        this.daysOfMonth = days[days.length-1]
      }
      return days
    },
    calculateIntervals: function () {
      if (this.period == 'if_needed') {
        this.intervals = [];
      } else {
        let _intervals = [];

        if (!this.beingRepeatedForEvery || parseInt(this.beingRepeatedForEvery) === 0) {
          _intervals.push({ intervalOn: true, interval: `P-1${this.period}` });
          this.intervals = _intervals;
          return;
        } else if (parseInt(this.beingRepeatedForEvery) < 0) {
          _intervals.push({ intervalOn: true, interval: `P0${this.period}` });
          this.intervals = _intervals;
          return;
        }

        if (parseInt(this.beingRepeatedForEvery) - 1 > 0) {
          const rest = parseInt(this.beingRepeatedForEvery) - 1;

          _intervals.push({ intervalOn: false, interval: `P${rest}${this.period}` })
        }

        _intervals.push({ intervalOn: true, interval: `P1${this.period}` });

        this.intervals = _intervals;
      }
    },
    submit() {
      this.calculateIntervals();

      if (!this.intervals.length) {
        this.$emit('input', null);
      } else {
        this.$emit('input', {
          intervals: this.intervals,
          monthsOfYear: this.monthsOfYear,
          daysOfMonth: this.daysOfMonth,
          daysOfWeek: this.daysOfWeek,
          note: null
        });
      }
    },
    orderDaysOfWeek(arr) {
      arr.sort((a, b) => {
          return a > b ? 1 : -1;
      });
      return arr;
    },
    setInitialData() {
      if (!this.schedule) {
        this.period = 'if_needed';
        this.setWatchers();
        return;
      }

      const intervalText = this.schedule.intervals && this.schedule.intervals[0]?.interval || null;
      this.intervals = this.schedule.intervals;
      this.monthsOfYear = this.schedule.monthsOfYear ? this.schedule.monthsOfYear[0] : null;
      this.daysOfMonth = this.schedule.daysOfMonth ? this.schedule.daysOfMonth[0] : null;
      this.daysOfWeek = this.schedule.daysOfWeek
      this.period = intervalText ? intervalText.charAt(intervalText.length - 1) : 'D'
      this.beingRepeatedForEvery = this.schedule.intervals && this.schedule.intervals.length > 1
        ? intervalText ? parseInt(intervalText.substr(1, 1)) + 1 : 1
        : intervalText ? parseInt(intervalText.substr(1, 1)) : 1

      this.setWatchers();
    },

    setWatchers() {
      const watchKeys = [
        'monthsOfYear',
        'daysOfMonth',
        'beingRepeatedForEvery',
        'daysOfWeek',
        'period'
      ];
      watchKeys.forEach(key => {
        this.$watch(key, {
          deep: true,
          handler: function(current, previous) {
            if (key === 'daysOfWeek' && current !== previous) {
              this.daysOfWeek = this.orderDaysOfWeek(current);
            }
            if (key === 'period' && current !== previous) {
              this.monthsOfYear = null;
              this.daysOfMonth = null;
              this.daysOfWeek = [];
              this.intervals = [];
            }
            this.submit();
          }
        })
      })
    }
  },
  mounted() {
    this.setInitialData();
  }
};
</script>

<style lang="scss">
.medication-frequency__input {
  padding: 8px 12px;
  border-collapse: collapse;
  border-color: rgba(0, 0, 0, 0.38);
  border-style: solid;
  border-width: 1px;
  border-radius: 4px;
  font-weight: 600;
  font-family: "Poppins", sans-serif;
  font-size: 0.9rem;
  transition-duration: 0.3s;
  transition-property: color, border-width;
  transition-timing-function: cubic-bezier(0.25, 0.8, 0.25, 1);
  min-width: 200px;
  width: 200px;
  min-height: 40px;
  cursor: pointer;

  &:hover {
    border-color: rgba(0, 0, 0, 0.86);
  }
}

.medication-frequency__input_disabled {
  background-color: #f6f6f6;
  border: 1px #f6f6f6 solid !important;
  cursor: default;
  pointer-events: none;
}

.medication-frequency__repeated-for {
  min-width: 60px;
  max-width: 60px;
  margin-right: 24px !important;
}

.medication-frequency__repeated-for-period {
  max-width: 200px;
}

.medication-frequency__title {
  height: 48px;
  & p {
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 0;
  }
}

.medication-frequency__wrapper {
  max-width: 284px;
}
</style>
